/**
 * AutoPreviewPage — the FREE-tier Auto-generate preview, shown on a PROJECT-PAGE-style
 * screen (the project sidebar + the SAME 18× GeneratingPlayer loader a paid render uses),
 * NOT a cramped Home overlay. Flow: fake "generating" loader → blurred "your video is
 * ready" gate → paywall. No order is created and nothing reaches HeyGen — it's a pure
 * conversion funnel. On subscribe we stash the exact intent so the real one-shot render
 * auto-fires the instant they return as Pro (HomePage's auto-resume effect).
 *
 * Reached via navigate('/auto-preview', { state }) from HomePage.submitAuto for free users,
 * so Auto-generate feels like the real project experience instead of a Home pop-up.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, PanelLeft } from 'lucide-react';
import { useUserStore } from '@/store/userStore';
import { useActiveOrg } from '@/store/workspaceStore';
import { v2Service } from '@/api/v2Service';
import { GeneratingPlayer } from '@/v2/components/GeneratingPlayer';
import { PreviewReadyGate } from '@/v2/components/PreviewFunnel';
import { ProjectHistorySidebar } from '@/v2/components/ProjectHistorySidebar';

type PreviewState = {
  prompt?: string;
  durationSec?: number;
  // Generic resume stash so BOTH auto-generate and manual share this page: on subscribe we
  // write `intent` to localStorage[stashKey] and bounce to upgrade with `fromParam`, so
  // HomePage's matching auto-resume effect re-fires the exact render once they're Pro.
  stashKey?: string;
  intent?: Record<string, unknown>;
  fromParam?: string;
};

export function AutoPreviewPage() {
  const { t } = useTranslation('v2');
  const navigate = useNavigate();
  const location = useLocation();
  const seed = (location.state || {}) as PreviewState;
  const userId = useUserStore((s) => s.userId);
  const user = useUserStore((s) => s.user);
  // Team workspaces: show the org's POOLED credits in team context.
  const activeOrg = useActiveOrg(user);
  const credits = activeOrg ? Math.floor(activeOrg.remainingCredit) : (user?.remainingCredit ?? 0);

  const [phase, setPhase] = useState<'generating' | 'preview'>('generating');
  const [cover, setCover] = useState<string | undefined>(undefined);
  // Open on desktop (pushes content); closed on mobile, where the sidebar is a fixed
  // overlay drawer (matches AgentProjectPage) so the funnel keeps the full screen width.
  const [historyOpen, setHistoryOpen] = useState(() => typeof window === 'undefined' || window.matchMedia('(min-width: 1024px)').matches);
  const started = useRef(false);

  // Fetch the cheap cover (our own model) once — shown blurred behind the loader and then
  // in the ready-gate. Falls back to a gradient if it fails. No HeyGen, no order.
  useEffect(() => {
    if (!seed.prompt || !userId) { navigate('/', { replace: true }); return; }
    if (started.current) return;
    started.current = true;
    v2Service.generatePlanCover({ userId, concept: seed.prompt })
      .then((r) => setCover(r.cover_image_url))
      .catch(() => { /* gradient fallback in PreviewReadyGate */ });
  }, [seed.prompt, userId, navigate]);

  const title = (seed.prompt || '').trim().slice(0, 60) || t('autoPreview.defaultTitle');

  const onSubscribe = () => {
    // Persist the request so it auto-fires after Stripe succeeds (auto OR manual).
    if (seed.stashKey && seed.intent) {
      try { localStorage.setItem(seed.stashKey, JSON.stringify({ ...seed.intent, ts: Date.now() })); }
      catch { /* private mode / quota — auto-resume just won't trigger */ }
    }
    navigate(`/subscription?context=video_ready&${seed.fromParam || 'from_auto=1'}`);
  };

  return (
    <div className="h-screen flex bg-[#121218] relative overflow-hidden">
      {/* Same project sidebar as the real project page. */}
      <ProjectHistorySidebar open={historyOpen} onToggle={() => setHistoryOpen((v) => !v)} />

      {/* Mobile backdrop — the history sidebar is an overlay drawer on phones; tap to close. */}
      {historyOpen && (
        <div className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm lg:hidden" onClick={() => setHistoryOpen(false)} />
      )}

      <div className="relative z-10 flex-1 flex flex-col min-w-0">
        {/* Top bar — mirrors AgentProjectPage so this reads as a real project. */}
        <div className="h-14 shrink-0 flex items-center justify-between gap-2 px-3 sm:px-4 border-b border-glass-border bg-surface/40 backdrop-blur-md">
          <div className="flex items-center gap-2 min-w-0">
            {!historyOpen && (
              <button onClick={() => setHistoryOpen(true)} className="w-9 h-9 rounded-lg flex items-center justify-center text-text-tertiary hover:text-text-primary hover:bg-white/5 transition-colors" aria-label={t('autoPreview.showProjects')}>
                <PanelLeft className="w-[18px] h-[18px]" strokeWidth={1.75} />
              </button>
            )}
            <button onClick={() => navigate('/')} className="w-9 h-9 rounded-xl bg-gradient-to-br from-surface-light to-surface border border-glass-border flex items-center justify-center text-text-secondary hover:text-white hover:border-primary/50 transition-all" aria-label={t('autoPreview.back')}>
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="flex items-center px-3.5 py-1.5 rounded-full border border-glass-border bg-surface">
              <span className="text-sm font-semibold text-text-primary max-w-[40vw] truncate">{title}</span>
            </div>
          </div>
          <button onClick={() => navigate(activeOrg ? '/team?tab=billing' : '/subscription')} className="flex items-center gap-2 px-4 py-2 rounded-3xl bg-surface border border-primary/30 hover:border-primary/50 transition-all" title={t('autoPreview.remainingCreditsTooltip')}>
            <span className="text-sm text-text-secondary hidden sm:inline">{activeOrg ? t('header.teamCredits') : t('autoPreview.credits')}</span>
            <span className="px-3 py-1 rounded-xl bg-gradient-to-r from-primary to-accent text-white text-sm font-bold tabular-nums shadow-[0_0_12px_rgba(139,92,246,0.4)]">{credits}</span>
          </button>
        </div>

        {/* Body — the SAME loader → ready-gate the project page uses, inline (no overlay). */}
        <div className="flex-1 flex min-h-0 justify-center">
          <div className="w-full max-w-4xl flex flex-col h-full min-h-0">
            <div className="flex-1 overflow-y-auto px-4 py-6 flex items-center">
              {phase === 'generating' ? (
                <div className="w-full">
                  <GeneratingPlayer durationSec={seed.durationSec || 15} blurImageUrl={cover} fast onDone={() => setPhase('preview')} />
                </div>
              ) : (
                <div className="w-full flex justify-center">
                  <PreviewReadyGate title={title} imageUrl={cover} onSubscribe={onSubscribe} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
